/**
 * Canonical traces/logs URLs.
 * Studio: /agent-studio/traces/:id
 * Monitoring (deployments, guardrails, …): /monitoring/traces/:id
 * Registry external (Copilot, Foundry, …): same path + ?registryExternal=1
 * Never look up traces by agent name — the GUID is the unique id.
 */

export interface TracesPathOptions {
  registryExternal?: boolean
  environment?: string
  /** studio | registry | deployment | agent_store */
  traceOrigin?: string
  /** Deployment views also show traces written before origin was stamped. */
  includeUnscopedOrigin?: boolean
  /** Studio views also show traces written before an environment was stamped. */
  includeUnsetEnvironment?: boolean
  /** Child (or deep-link) trace — never the parent span's trace_id. */
  traceId?: string
  sessionId?: string
  from?: string
}

export function isMonitoringTracesPath(pathname: string): boolean {
  return pathname.startsWith('/monitoring/traces/')
}

function tracesPathWithBase(
  base: '/agent-studio/traces' | '/monitoring/traces',
  agentId: string,
  opts?: TracesPathOptions,
): string {
  const params = new URLSearchParams()
  if (opts?.registryExternal) params.set('registryExternal', '1')
  if (opts?.environment) params.set('env', opts.environment)
  if (opts?.traceOrigin) params.set('origin', opts.traceOrigin)
  if (opts?.includeUnscopedOrigin) params.set('include_unscoped_origin', 'true')
  if (opts?.includeUnsetEnvironment) params.set('include_unset_environment', 'true')
  if (opts?.traceId) params.set('trace', opts.traceId)
  if (opts?.sessionId) params.set('session', opts.sessionId)
  if (opts?.from) params.set('from', opts.from)
  const qs = params.toString()
  const querySuffix = qs ? `?${qs}` : ''
  return `${base}/${agentId}${querySuffix}`
}

export function studioTracesPath(agentId: string, opts?: TracesPathOptions): string {
  return tracesPathWithBase('/agent-studio/traces', agentId, opts)
}

export function monitoringTracesPath(agentId: string, opts?: TracesPathOptions): string {
  return tracesPathWithBase('/monitoring/traces', agentId, opts)
}

/** Keep nested trace drill-down on the same surface (studio vs monitoring). */
export function tracesPathForPathname(
  pathname: string,
  agentId: string,
  opts?: TracesPathOptions,
): string {
  return isMonitoringTracesPath(pathname)
    ? monitoringTracesPath(agentId, opts)
    : studioTracesPath(agentId, opts)
}

/** Read nested-trace deep-link query params (`?trace=` / `?session=`). */
export function nestedTraceQueryFromSearch(search: string): {
  initialTraceId: string | null
  initialSessionId: string | null
} {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const trace = params.get('trace')
  const session = params.get('session')
  return {
    initialTraceId: trace && trace.trim() ? trace.trim() : null,
    initialSessionId: session && session.trim() ? session.trim() : null,
  }
}

/** Scope View Traces to the surface that opened it. Explicit query params win. */
export function traceScopeFromSearch(search: string, from?: string | null): {
  environment?: string
  traceOrigin?: string
  includeUnscopedOrigin?: boolean
  includeUnsetEnvironment?: boolean
} {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const env = params.get('env')
  const origin = params.get('origin')
  if (env || origin) {
    return {
      environment: env || undefined,
      traceOrigin: origin || undefined,
      includeUnscopedOrigin: params.get('include_unscoped_origin') === 'true',
      includeUnsetEnvironment: params.get('include_unset_environment') === 'true',
    }
  }
  if (from?.startsWith('/monitoring')) {
    return { traceOrigin: 'deployment', includeUnscopedOrigin: true }
  }
  if (params.get('registryExternal') === '1' || from?.startsWith('/agent-registry')) {
    return { environment: 'DEV', traceOrigin: 'registry' }
  }
  if (from?.includes('/agent-store')) {
    return { environment: 'PROD', traceOrigin: 'agent_store' }
  }
  if (from?.startsWith('/agent-studio') || from?.startsWith('/projects')) {
    return {
      environment: 'TEST',
      traceOrigin: 'studio',
    }
  }
  return {}
}

export function studioLogsPath(
  agentId: string,
  opts?: { registryExternal?: boolean; from?: string }
): string {
  const params = new URLSearchParams()
  if (opts?.registryExternal) params.set('registryExternal', '1')
  if (opts?.from) params.set('from', opts.from)
  const qs = params.toString()
  const querySuffix = qs ? `?${qs}` : ''
  return `/agent-studio/logs/${agentId}${querySuffix}`
}

/** Store / listing traces: XOR `external_agent_id` vs native `agent_id`. Never name lookup. */
export function tracesPathForListing(listing: {
  agent_id?: string | null
  external_agent_id?: string | null
}): string | null {
  const scope = { environment: 'PROD', traceOrigin: 'agent_store', from: '/agent-store' }
  if (listing.external_agent_id) {
    return studioTracesPath(listing.external_agent_id, { registryExternal: true, ...scope })
  }
  if (listing.agent_id) {
    return studioTracesPath(listing.agent_id, scope)
  }
  return null
}

/**
 * Label for the traces/logs back button, derived from the `from` route passed in
 * navigation state. `backTarget` already honoured `from`, but the label fell
 * through to "Agent Studio" for every origin except chat and guardrails — so
 * arriving from the Registry or a Project showed a button that named the wrong
 * page it would return to.
 *
 * Order matters: `/agent-registry/chat/:id` and `/agent-registry/guardrails/:id`
 * must resolve to Chat / Guardrails, not Agent Registry.
 */
export function observabilityBackLabel(from?: string | null): string {
  if (!from) return 'Agent Studio'
  if (from.includes('/chat/')) return 'Chat'
  if (from.includes('/guardrails/')) return 'Guardrails'
  if (from.startsWith('/agent-registry')) return 'Registry'
  // Chat / traces opened from Monitoring → Deployments should name that tab.
  if (from.startsWith('/monitoring')) {
    return from.includes('tab=deployments') ? 'Deployments' : 'Monitoring'
  }
  if (from.startsWith('/projects')) return 'Project'
  if (from.startsWith('/benchmark-studio')) return 'Benchmark Studio'
  return 'Agent Studio'
}
